import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { useContext, useState } from "react";
import { SavedSearch } from "../../../../types/saved-search/SavedSearch";
import { z } from "zod";
import Form from "../../../../components/common/Form/Form";
import LoadingIndicator from "../../../../components/common/LoadingIndicator/LoadingIndicator";
import {
  DEFAULT_SEARCH_VALUES,
  SearchFormValues,
} from "../../../../types/search/SearchFormValues";
import { AuthContext } from "../../../../context/AuthContext";
import { GetInteractionsRequest } from "../../../../api/interaction";
import {
  useCreateSavedSearch,
  useUpdateSavedSearch,
} from "../../../../hooks/useSavedSearch";
import { useParams } from "react-router-dom";
import { useAlert } from "../../../../context/AlertContext";
import { CreateSavedSearchRequest } from "../../../../api/savedSearch";
import { isEqual } from "lodash";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface SaveSearchDialogProps {
  currentSavedSearch: SavedSearch | null;
  searchValues: SearchFormValues;
  getSearchRequest: (saerchValues: SearchFormValues) => GetInteractionsRequest;
  handleSavedSearchChange: (savedSearch: SavedSearch | null) => void;
}

export type SaveSearchFormValues = {
  name: string;
};

const schema = z.object({
  name: z.string().min(1, "Name is required"),
});

export default function SaveSearchDialog({
  currentSavedSearch,
  searchValues,
  getSearchRequest,
  handleSavedSearchChange,
}: Readonly<SaveSearchDialogProps>) {
  const { t } = useTranslation("search");
  const [opened, setOpened] = useState(false);

  const { customerId } = useParams();

  const queryClient = useQueryClient();

  const showAlert = useAlert();

  const auth = useContext(AuthContext);
  const authUser = auth.user;

  const createSavedSearchMutation = useCreateSavedSearch();
  const updateSavedSearchMutation = useUpdateSavedSearch();

  const title = currentSavedSearch
    ? "Update search name?"
    : "Save search template?";

  const tooltipTitle = currentSavedSearch
    ? t("search:updateSelectedTemplateAndSearch")
    : t("search:createNewTemplateWithFilters");

  const action = currentSavedSearch ? "update" : "create";

  const onSubmit = async (data: SaveSearchFormValues) => {
    if (currentSavedSearch) {
      const updatedSavedSearch: SavedSearch = {
        ...currentSavedSearch,
        name: data.name,
        search: getSearchRequest(searchValues),
      };

      try {
        const updatedSearch = await updateSavedSearchMutation.mutateAsync({
          customerId: customerId!,
          savedSearchId: currentSavedSearch.savedSearchId,
          name: updatedSavedSearch.name,
          searchRequest: updatedSavedSearch.search,
        });

        if (!updatedSearch) {
          showAlert("There was an issue updating the saved search", "error");
          return;
        }
        showAlert("Saved search updated successfully", "success");

        handleSavedSearchChange(updatedSavedSearch);
        setOpened(false);
      } catch (error) {
        showAlert("There was an issue updating the saved search", "error");
        console.error("Unknown error updating saved search:", error);
      }
    } else if (authUser) {
      const request: CreateSavedSearchRequest = {
        customerId: customerId!,
        name: data.name,
        searchRequest: getSearchRequest(searchValues),
      };

      try {
        const newSavedSearch = await createSavedSearchMutation.mutateAsync(
          request
        );

        if (!newSavedSearch) {
          showAlert("There was an issue creating the saved search", "error");
          return;
        }

        // Update the query cache with the new saved search
        queryClient.setQueryData<SavedSearch[]>(
          ["savedSearches", customerId, authUser.userId],
          (old) => (old ?? []).concat(newSavedSearch)
        );
        handleSavedSearchChange(newSavedSearch);
        showAlert("Saved search created successfully", "success");
        setOpened(false);
      } catch (error) {
        showAlert("There was an issue creating the saved search", "error");
        console.error("Unknown error deleting saved search:", error);
      }
    }
  };

  return (
    <>
      <Tooltip title={tooltipTitle} arrow placement="bottom">
        <span style={{ display: "flex", width: "100%" }}>
          <Button
            variant="outlined"
            size="large"
            sx={{ mr: 1, ml: 1, mb: 2 }}
            disabled={isEqual(searchValues, DEFAULT_SEARCH_VALUES)}
            onClick={() => setOpened(true)}
            fullWidth
          >
            {currentSavedSearch ? t("updateAndSearch") : t("saveAndSearch")}
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={opened}
        onClose={() => setOpened(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          <Form
            id={`${action}-saved-search-form`}
            schema={schema}
            onSubmit={onSubmit}
            options={{
              defaultValues: {
                name: currentSavedSearch ? currentSavedSearch.name : "",
              },
            }}
          >
            {(form) => {
              const { formState, register } = form;
              const { isSubmitting, errors } = formState;

              return (
                <>
                  <FormGroup>
                    <TextField
                      label="Name"
                      error={!!errors.name}
                      required
                      helperText={errors.name?.message}
                      {...register("name", {
                        required: "Name is required",
                      })}
                    />
                  </FormGroup>
                  <DialogActions>
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      gap={2}
                    >
                      <Button
                        variant="outlined"
                        disabled={isSubmitting}
                        onClick={() => setOpened(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting || formState.isLoading}
                        variant="contained"
                      >
                        {isSubmitting ? <LoadingIndicator /> : "Submit"}
                      </Button>
                    </Stack>
                  </DialogActions>
                </>
              );
            }}
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
